import {AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild,} from '@angular/core';
import {UserList, UserQuery} from '../repository/entity/user.entity';
import {UserService} from '../service/user.service';
import {MatPaginator} from '@angular/material/paginator';
import {catchError, firstValueFrom, map, of, startWith, switchMap,} from 'rxjs';
import {MatTableDataSource} from '@angular/material/table';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {DialogConfirmationComponent} from '../../../shared/dialog-confirmation/dialog-confirmation.component';
import {NgxSpinnerService} from 'ngx-spinner';
import {ToastrService} from 'ngx-toastr';
import {BaseService} from '../../base/services/base.service';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
})
export class UserComponent implements OnInit, AfterViewInit {
  destroyRef = inject(DestroyRef);

  displayedColumns: string[] = [
    'username',
    'fullName',
    'email',
    'isActive',
    'actions',
  ];

  totalData: number;
  userData: UserList[];

  dataSource = new MatTableDataSource<UserList>();

  isLoading = false;
  searchData: string = "";
  @ViewChild('paginator') paginator: MatPaginator;
  pageSizes = [5, 10, 50];

  constructor(
    public userService: UserService,
    private baseService: BaseService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private toastService: ToastrService
  ) {
  }

  onEnterSearch() {
    this.updatePagination$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userData) => {
        this.userData = userData;
        this.dataSource = new MatTableDataSource(this.userData);
      });
  }

  ngOnInit() {
    this.baseService.setSupportingText(
      'You can view, create, update and delete user from this page.'
    );
  }

  getTableData$(page: number, limit: number) {
    const query: UserQuery = {
      page,
      limit,
      q: this.searchData,
    };
    return this.userService.fetchListUser(query);
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;

    this.updatePagination$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userData) => {
        this.userData = userData;
        this.dataSource = new MatTableDataSource(this.userData);
      });

    this.cdr.detectChanges();
  }

  updatePagination$() {
    return this.paginator.page
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoading = true;
          return this.getTableData$(
            this.paginator.pageIndex + 1,
            this.paginator.pageSize
          ).pipe(catchError(() => of(null)));
        }),
        map((data) => {
          if (data?.result == null) return [];
          this.totalData = data.result.count;
          this.isLoading = false;
          return data.result.data;
        })
      )
  }

  onClickCreate() {
    this.router.navigate(['user/create']);
  }

  onClickEdit(user: UserList) {
    this.router.navigate(['user/update', user.username]);
  }

  onClickDelete(user: UserList) {
    const dialogRef = this.dialog.open(DialogConfirmationComponent, {
      data: {
        title: 'Delete Data',
        message: `Are you sure delete this data ${user.fullName}`,
      },
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (value === 'ok') this.onDelete(user.username);
    });
  }

  async onDelete(username: string) {
    await this.spinner.show();
    const deleteAction$ = this.userService.deleteUser(username).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((value) => {
        if (value?.result) {
          return this.updatePagination$();
        } else {
          return of(null);
        }
      })
    );

    const response = await firstValueFrom(deleteAction$);
    if (response?.length) {
      this.userData = response;
      this.dataSource = new MatTableDataSource(this.userData);
      this.toastService.success('Succesfully deleted!');
      await this.spinner.hide();
    }
  }

  onClickRestore(user: UserList) {
    const dialogRef = this.dialog.open(DialogConfirmationComponent, {
      data: {
        title: 'Restore Data',
        message: `Are you sure restore this data ${user.fullName}`,
      },
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (value === 'ok') this.onRestore(user.username);
    });
  }

  async onRestore(username: string) {
    this.spinner.show();
    const restoreAction$ = this.userService.restoreUser(username).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((value) => {
        if (value?.result) {
          return this.updatePagination$();
        } else {
          return of(null);
        }
      })
    );

    const response = await firstValueFrom(restoreAction$);
    if (response?.length) {
      this.userData = response;
      this.dataSource = new MatTableDataSource(this.userData);
      this.toastService.success('Succesfully restored!');
      await this.spinner.hide();
    }
  }
}
